import {
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAuth, useT } from '@powerportalspro/react';
import {
  MainGrid,
  PagingMode,
  type CustomViewDefinition,
} from '@powerportalspro/react-fluent';

// Same vertical stack as the standalone main demo — warning bar above the
// grid, small Fluent v9 gap. Mirrors the Blazor `<FluentStack VerticalGap="10">`.
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
});

// Virtualize-mode (infinite-scroll) variant of `MainGridMainSample`. The
// view shape is identical so users comparing the two demos see the same
// columns and data; the only meaningful change is `pagingMode` + a
// smaller `pageSize` so scrolling actually exercises the accumulator
// (the default 25 would typically fill the visible area in one go).
//
// In virtualize mode the pager footer is hidden — rows accumulate as the
// user scrolls past the rendered bottom — and a "Showing 1-N of M"
// counter replaces it once a per-grid total is available.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.MainGrid.MainGridVirtualizeDemo';

const ALL_CONTACTS_VIEW_ID = '28299C6F-EBC0-4206-9E11-A373D4C9891F';
const MY_CONTACTS_VIEW_ID = '25999A64-A871-4200-A38C-22ACD531F1A2';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function buildAllContactsView(currentUserId: string): CustomViewDefinition {
  return {
    id: ALL_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName: 'Active Contacts',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <filter type="or">
          <condition attribute="ownerid" operator="eq" value="e3189fd4-eb34-f111-88b4-6045bd02c5ed" />
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${currentUserId}" />
        </filter>
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
    ],
  };
}

function buildMyContactsView(currentUserId: string): CustomViewDefinition {
  return {
    id: MY_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName: 'My Contacts',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <filter type="and">
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${currentUserId}" />
        </filter>
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
    ],
  };
}

export function MainGridVirtualizeSample() {
  const styles = useStyles();
  const auth = useAuth();
  const t = useT();
  const currentUserId =
    auth.status === 'authenticated' ? auth.user.userId ?? EMPTY_GUID : EMPTY_GUID;

  const customViews: CustomViewDefinition[] = [
    buildAllContactsView(currentUserId),
    buildMyContactsView(currentUserId),
  ];

  return (
    <div className={styles.root}>
      <MessageBar
        intent="warning"
        layout="multiline"
        style={{ gridTemplateRows: 'auto', minHeight: 'auto' }}
      >
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        minHeight="400px"
        maxHeight="400px"
        pagingMode={PagingMode.Virtualize}
        pageSize={15}
        customViewDefinitions={customViews}
        viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
      />
    </div>
  );
}
